/* eslint-disable eqeqeq */
import React from 'react';
import ReactSelect, { createFilter } from 'react-select';
import './ReactSelect.scss';
import { SelectOption } from '../../types';

interface Props<T = string> {
  options: SelectOption<T>[]
  onChange: (option: SelectOption<T>) => void
  placeholder: string
  value: T | undefined
  params: {
    width: number
    height: number
  }
  isClearable?: boolean
  disabled?: boolean
  error?: boolean
  defaultValue?: T | undefined
  filterByLabel?: boolean
  findValueById?: boolean
  onInputChange?: (value: string) => void
  maxOptionsCount?: number
  onClearChange?: () => void
  formatOptionLabel?: (option: SelectOption<T>) => React.ReactNode
}

export function ReactSelectDefault<T = string>({
  options,
  onChange,
  placeholder,
  value,
  params,
  isClearable = false,
  disabled = false,
  error = false,
  defaultValue = undefined,
  onInputChange = undefined,
  filterByLabel = undefined,
  findValueById = undefined,
  onClearChange = undefined,
  maxOptionsCount = undefined,
  formatOptionLabel = undefined,
}: Props<T>) {
  const [localOptions, setLocalOptions] = React.useState(options);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  const getCurrentValue = (): SelectOption<T> | null => {
    if (findValueById) {
      if (value == null) return null;
      const valueId = (value as any)?.id;
      if (valueId == null) return null;
      return (localOptions as any).find((i: any) => (i?.value as any)?.id == valueId) || null;
    }

    return localOptions.find((i) => (i as any).value == (value as any)) || null;
  };

  const onChangeHandler = (option: SelectOption<T> | null, actionMeta: any) => {
    if (actionMeta.action === 'clear') {
      if (onClearChange) {
        onClearChange();
      }
      return;
    }

    onChange(option === null ? { label: '', value: '' as T } : option);
  };

  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;

  return (
    <div
      className={error ? 'reactSelectError' : 'reactSelect'}
      style={
        {
          width: `${params.width}px`,
          height: `${params.height}px`,
        }
      }
    >
      <ReactSelect<SelectOption<T>>
        onInputChange={(value) => onInputChange && onInputChange(value)}
        options={localOptions}
        inputId={value?.toString() ?? ''}
        placeholder={placeholder}
        isSearchable
        isLoading={isLoading}
        value={getCurrentValue()}
        defaultValue={defaultValue ? localOptions.find((i) => (i as any).value == (defaultValue as any)) : undefined}
        onChange={onChangeHandler}
        isDisabled={disabled}
        openMenuOnFocus
        filterOption={createFilter({
          ignoreCase: true,
          ignoreAccents: true,
          matchFrom: 'any',
          stringify: (option) => (filterByLabel ? option.label : `${option.label} ${option.value}`),
          trim: true,
        })}
        menuPlacement="auto"
        isClearable={isClearable}
        key={`${(value as any) ?? ''}-${localOptions.length}`}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        formatOptionLabel={formatOptionLabel ? (opt) => formatOptionLabel(opt) : undefined}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 10000 }),
          menu: (base) => ({ ...base, zIndex: 10000 }),
        }}
      />
    </div>
  );
}
